/**
 * storeSchedule.js — pure helper for the Schedule column in the
 * distributor Stores list.
 *
 * scheduleStatus({ end_date, is_permanent_store, now, tz })
 *   -> { tone, label }
 *
 * tone: 'red' | 'yellow' | 'green' | 'permanent' | 'empty'
 * label: human string suitable for either a v-chip or plain text.
 *
 * CRITICAL: is_permanent_store === true ALWAYS wins. Permanent stores
 * render an em-dash (no date and no "Permanent" label) — even if
 * end_date is non-null (seed data may carry stale end_date values on
 * permanent stores; the Open/Close fields in the editor are disabled,
 * but the value persists).
 *
 * The Mode column already renders "Permanent" for these rows, so the
 * Schedule column intentionally avoids duplicating that label —
 * "Permanent / Permanent" side-by-side per row is noise.
 *
 * Rules (first match wins):
 *   is_permanent_store === true                  -> permanent + em-dash
 *   end_date null / undefined / ""               -> empty + em-dash
 *   end_date at or before now (wall clock in tz) -> red, "Ended <Mon Day>"
 *   end_date later today                         -> yellow, "Ends today"
 *   end_date next calendar day                   -> yellow, "Ends tomorrow"
 *   end_date < now + 7d                          -> yellow, "Ends <Mon Day>"
 *   end_date >= now + 7d                         -> green, "Ends <Mon Day>"
 *
 * `now` is injectable for test determinism (defaults to new Date()).
 * `tz` is the IANA zone the store's dates are read in -- the row's
 * `schedule_timezone`, i.e. the distributor's. end_date is a NAIVE wall
 * clock in that zone, so its calendar day is its own and is never
 * converted; only `now` is read in `tz` to find "today". (Converting the
 * end as an instant put a midnight close on the previous day.) When `tz`
 * is omitted, "today" is the browser's.
 *
 * "Ended" is decided by hasClosedInZone, the rule the Status pill uses
 * (deriveStoreStatus), so a store whose close passed earlier today reads
 * Ended beside its Closed pill rather than "Ends today".
 */

import { hasClosedInZone, naiveWallClock, wallClockInZone } from "@/utils/storeClock";

// Em-dash kept as an escape sequence so this source file stays ASCII-only
// (per CLAUDE.md). The rendered character is U+2014.
const EM_DASH = "—";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * { year, month, day } of a "YYYY-MM-DD HH:mm:ss" wall clock.
 */
function calendarParts(wall) {
    return {
        year: Number(wall.slice(0, 4)),
        month: Number(wall.slice(5, 7)),
        day: Number(wall.slice(8, 10)),
    };
}

/**
 * Format a calendar date as "MMM D" (e.g., "Jun 14"). The parts are built
 * as a UTC instant and formatted in UTC, so no zone can shift the day.
 */
function formatMonthDay({ year, month, day }) {
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(year, month - 1, day)));
}

/**
 * Whole-day difference between two calendar dates, computed by treating
 * the {year, month, day} triples as UTC midnight instants. Returns
 * (endDay - nowDay), so positive = end is in the future.
 */
function dayDelta(endParts, nowParts) {
    const endMs = Date.UTC(endParts.year, endParts.month - 1, endParts.day);
    const nowMs = Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day);
    return Math.round((endMs - nowMs) / MS_PER_DAY);
}

export function scheduleStatus({
    end_date,
    is_permanent_store,
    now,
    tz,
} = {}) {
    if (is_permanent_store === true) {
        return { tone: "permanent", label: EM_DASH };
    }

    const end = naiveWallClock(end_date);
    if (!end) {
        return { tone: "empty", label: EM_DASH };
    }

    const nowDate = now instanceof Date ? now : new Date();

    const endParts = calendarParts(end);
    const nowParts = calendarParts(wallClockInZone(tz, nowDate));
    const delta = dayDelta(endParts, nowParts);

    const labelDate = formatMonthDay(endParts);

    if (hasClosedInZone(end, tz, nowDate)) {
        return { tone: "red", label: `Ended ${labelDate}` };
    }
    if (delta === 0) {
        return { tone: "yellow", label: "Ends today" };
    }
    if (delta === 1) {
        return { tone: "yellow", label: "Ends tomorrow" };
    }
    if (delta < 7) {
        return { tone: "yellow", label: `Ends ${labelDate}` };
    }
    return { tone: "green", label: `Ends ${labelDate}` };
}

export { EM_DASH };
